// src/shared/http/aws-client.js
//
// Axios instance for AWS API Gateway endpoints — replaces the original
// common/wisbotAwsApi.js.  Preserves the nested-body unwrap logic and
// uses the shared TokenService for auth headers.

import axios from 'axios';
import { TokenService } from './token';

const awsClient = axios.create({
  baseURL:
    (process.env.VUE_APP_AI_API_BASE_URL || '') +
    (process.env.VUE_APP_AI_API_STAGE || ''),
});

// Lazy-load ToastService to keep this module decoupled from PrimeVue.
const showToast = (severity, summary, detail) => {
  import('@/shared/composables/useToast')
    .then(({ showToast: notify }) => notify(severity, summary, detail))
    .catch(() => {});
};

// ── Request interceptor — inject JWT ────────────────────────────────
awsClient.interceptors.request.use(
  (config) => {
    const token = TokenService.getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error),
);

// ── Response interceptor — unwrap API Gateway envelope ──────────────
awsClient.interceptors.response.use(
  (response) => {
    const parsedData =
      typeof response.data === 'string'
        ? JSON.parse(response.data)
        : response.data;

    response.data = parsedData;

    if (parsedData.success) {
      response.data.body =
        typeof parsedData.body === 'string'
          ? JSON.parse(parsedData.body)
          : parsedData.body;
    }

    if (!parsedData?.success) {
      showToast(
        'error',
        'Error',
        parsedData.error?.message || 'An error occurred',
      );
      if (parsedData.error?.code === 401) {
        TokenService.removeToken();
      }
      throw new Error(parsedData.error?.message || 'An error occurred');
    }

    return response;
  },
  (error) => {
    if (error.response?.data?.body) {
      try {
        const errorBody =
          typeof error.response.data.body === 'string'
            ? JSON.parse(error.response.data.body)
            : error.response.data.body;
        throw new Error(errorBody.error?.message || 'An error occurred');
      } catch (parseError) {
        if (parseError.message !== 'An error occurred') {
          console.error('Error parsing error response:', parseError);
        }
      }
    }
    throw new Error(error.message || 'An error occurred');
  },
);

export default awsClient;
